import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

/**
 * A synchronous, `Storage`-compatible key/value wrapper that works across both
 * web and native (Capacitor) platforms.
 *
 * - On the **web** it delegates directly to `localStorage`.
 * - On **native** it reads from an in-memory cache (hydrated from Capacitor
 *   `Preferences` during bootstrap via {@link initAppStorage}) and mirrors every
 *   write back to `Preferences` asynchronously. This avoids the tight
 *   `localStorage` quota of native WebViews while keeping a synchronous API,
 *   which is required by callers such as `pinia-plugin-persistedstate`.
 *
 *
 * The exposed shape ({@link getItem}/{@link setItem}/{@link removeItem}) matches
 * the `StorageLike` interface expected by `pinia-plugin-persistedstate`, so the
 * same instance can be passed as the plugin's global `storage` option.
 */

const isNative = Capacitor.isNativePlatform();

/**
 * Mirrors persisted values in memory so reads can be served synchronously on
 * native. Always empty on the web (localStorage is used directly there).
 */
const cache = new Map<string, string>();

// On the web there is nothing to hydrate, so we are "ready" immediately.
let hydrated = !isNative;

/**
 * Hydrates the in-memory cache from native `Preferences`. Must be awaited during
 * app bootstrap *before* any synchronous read (e.g. before Pinia stores are
 * instantiated). No-op on the web.
 */
export const initAppStorage = async (): Promise<void> => {
  if (!isNative || hydrated) {
    return;
  }

  const { keys } = await Preferences.keys();
  const entries = await Promise.all(
    keys.map(async (key) => {
      const { value } = await Preferences.get({ key });
      return [key, value] as const;
    }),
  );
  entries.forEach(([key, value]) => {
    if (value !== null) {
      cache.set(key, value);
    }
  });

  hydrated = true;
};

export const appStorage = {
  getItem(key: string): string | null {
    if (isNative) {
      return cache.has(key) ? (cache.get(key) as string) : null;
    }
    return localStorage.getItem(key);
  },

  setItem(key: string, value: string): void {
    if (isNative) {
      cache.set(key, value);
      Preferences.set({ key, value });
      return;
    }
    localStorage.setItem(key, value);
  },

  removeItem(key: string): void {
    if (isNative) {
      cache.delete(key);
      Preferences.remove({ key });
      return;
    }
    localStorage.removeItem(key);
  },

  clear(): void {
    if (isNative) {
      cache.clear();
      Preferences.clear();
      return;
    }
    localStorage.clear();
  },
};

export type AppStorage = typeof appStorage;
